<template>
  <header class="app-header" :class="{ 'is-sticky': isSticky, 'user-logged-in': isLoggedIn }">
    <!-- Top Spacing -->
    <div class="header-spacing-top"></div>

    <!-- Main Header Bar -->
    <div class="header-main">
      <div class="header-container">
        <!-- Mobile Menu Toggle (opens sidebar) -->
        <button @click="toggleSidebar" class="mobile-toggle" aria-label="Open menu">
          <i class="fas fa-bars" aria-hidden="true"></i>
        </button>

        <!-- Categories Menu Toggle (Only visible on sticky/floating header - Desktop) -->
        <div v-if="isSticky" class="categories-menu-wrapper desktop-only">
          <button @click="toggleCategoriesMenu" class="categories-toggle" aria-label="Toggle categories menu">
            <i class="fas fa-bars" aria-hidden="true"></i>
          </button>
          <div v-show="showCategoriesMenu" class="categories-dropdown">
            <div
              v-for="category in categories"
              :key="category.slug"
              class="category-dropdown-group"
            >
              <a :href="'/' + category.slug" class="category-dropdown-item category-parent">
                <i :class="category.icon || 'fas fa-folder'"></i>
                <span>{{ category.name }}</span>
              </a>
              <div v-if="category.subcategories && category.subcategories.length" class="subcategories-list">
                <a
                  v-for="sub in category.subcategories"
                  :key="sub.slug"
                  :href="sub.link"
                  class="category-dropdown-item subcategory-item"
                >
                  {{ sub.name }}
                </a>
              </div>
            </div>
          </div>
        </div>

        <!-- Logo -->
        <div class="header-logo" :class="{ 'logo-shifted': isLoggedIn }">
          <a :href="route('home')">
            <img :src="logoUrl" :alt="props.appName" class="logo-image" width="150" height="40" />
          </a>
        </div>

        <!-- Search Bar -->
        <div class="header-search">
          <form @submit.prevent="handleSearch" class="search-form">
            <input
              v-model="searchQuery"
              type="search"
              placeholder="Search products, brands and categories"
              class="search-input"
            >
            <button type="submit" class="search-btn" aria-label="Search">
              <i class="fas fa-search" aria-hidden="true"></i>
            </button>
          </form>
        </div>

        <!-- Header Actions -->
        <div class="header-actions">
          <!-- User Menu or Auth Links (Desktop only) -->
          <template v-if="isLoggedIn">
            <div class="user-dropdown desktop-only">
              <button @click="toggleUserMenu" class="user-btn-auth">
                <img v-if="userAvatar" :src="userAvatar" :alt="userName" class="user-avatar" />
                <i v-else class="fas fa-user-circle"></i>
                <span class="user-name">{{ userName }}</span>
                <i class="fas fa-chevron-down dropdown-arrow"></i>
              </button>
              <div v-show="showUserMenu" class="dropdown-menu">
                <a :href="route('dashboard')" class="dropdown-item">Dashboard</a>
                <a :href="route('dashboard', 'orders')" class="dropdown-item">Orders</a>
                <a :href="route('dashboard', 'wishlist')" class="dropdown-item">Wishlist</a>
                <hr>
                <form @submit.prevent="handleLogout" class="logout-form">
                  <button type="submit" class="dropdown-item">Logout</button>
                </form>
              </div>
            </div>
          </template>
          <template v-else>
            <a :href="route('login')" class="login-btn desktop-only">
              <i class="fas fa-user"></i>
              <span>Login</span>
            </a>
            <a :href="route('register')" class="register-btn desktop-only">Register</a>
          </template>

          <!-- Help Center -->
          <a href="/help-center" class="help-link desktop-only">Help Center</a>

          <!-- Mobile Account (with dropdown when logged in) -->
          <div v-if="isLoggedIn" class="mobile-user-dropdown mobile-only">
            <button @click="toggleMobileUserMenu" class="mobile-user-btn">
              <img v-if="userAvatar" :src="userAvatar" :alt="userFirstName" class="user-avatar" />
              <i v-else class="fas fa-user-circle"></i>
              <span class="mobile-user-name">{{ userFirstName }}</span>
            </button>
            <div v-show="showMobileUserMenu" class="mobile-dropdown-menu">
              <a :href="route('dashboard')" class="dropdown-item">Dashboard</a>
              <a :href="route('dashboard', 'orders')" class="dropdown-item">Orders</a>
              <a :href="route('dashboard', 'wishlist')" class="dropdown-item">Wishlist</a>
              <hr>
              <form @submit.prevent="handleLogout" class="logout-form">
                <button type="submit" class="dropdown-item">Logout</button>
              </form>
            </div>
          </div>
          <a v-else :href="route('login')" class="mobile-account-btn mobile-only" aria-label="Login to your account">
            <i class="fas fa-user" aria-hidden="true"></i>
          </a>

          <!-- Cart -->
          <a :href="route('cart.view')" class="cart-btn" aria-label="Shopping cart">
            <i class="fas fa-shopping-cart" aria-hidden="true"></i>
            <span v-if="localCartCount > 0" class="cart-badge" aria-label="Items in cart">{{ localCartCount }}</span>
          </a>
        </div>
      </div>
    </div>

    <!-- Bottom Spacing -->
    <div class="header-spacing-bottom"></div>
  </header>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useAuthStore } from '../../store/auth'

// Props from Laravel
const props = defineProps({
  user: {
    type: Object,
    default: null
  },
  cartCount: {
    type: Number,
    default: 0
  },
  appName: {
    type: String,
    default: 'Ibomstore'
  },
  appUrl: {
    type: String,
    default: '/'
  },
  appLocale: {
    type: String,
    default: 'en'
  },
  categories: {
    type: Array,
    default: () => []
  }
})

const authStore = useAuthStore()

const showUserMenu = ref(false)
const showMobileUserMenu = ref(false)
const showCategoriesMenu = ref(false)
const searchQuery = ref('')
const isSticky = ref(false)

// Reactive cart count - initialized from prop but can be updated via events
const localCartCount = ref(props.cartCount)

// Use props.user directly for session-based auth
const isLoggedIn = computed(() => !!props.user)
const isAuth = computed(() => !!props.user)
const userName = computed(() => {
  const user = props.user
  return user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : ''
})
const userFirstName = computed(() => props.user?.first_name || '')
const userAvatar = computed(() => props.user?.image || null)
const logoUrl = computed(() => '/logos/logo.svg')

const route = (name, param = '') => {
  const routes = {
    'home': '/',
    'products.index': '/products',
    'products.search': '/search',
    'collections.index': '/collections',
    'trending': '/trending',
    'today-deals': '/today-deals',
    'cart.view': '/cart',
    'dashboard': param ? `/dashboard/${param}` : '/dashboard',
    'orders': '/orders',
    'login': '/login',
    'register': '/register'
  }
  return routes[name] || '/'
}

const toggleUserMenu = () => {
  showUserMenu.value = !showUserMenu.value
}

const toggleMobileUserMenu = () => {
  showMobileUserMenu.value = !showMobileUserMenu.value
}

const toggleSidebar = () => {
  window.dispatchEvent(new CustomEvent('toggle-sidebar'))
}

const toggleCategoriesMenu = () => {
  showCategoriesMenu.value = !showCategoriesMenu.value
}

const handleSearch = () => {
  if (searchQuery.value.trim()) {
    const baseUrl = route('products.search')
    window.location.href = `${baseUrl}?q=${encodeURIComponent(searchQuery.value)}`
  }
}

const handleLogout = () => {
  const form = document.createElement('form')
  form.method = 'POST'
  form.action = '/logout'
  const token = document.querySelector('meta[name="csrf-token"]')?.content
  const input = document.createElement('input')
  input.type = 'hidden'
  input.name = '_token'
  input.value = token
  form.appendChild(input)
  document.body.appendChild(form)
  form.submit()
}

onMounted(() => {
  if (props.user) {
    authStore.setUser(props.user)
  }

  if (props.cartCount !== undefined) {
    authStore.setCartCount(props.cartCount)
  }

  // Listen for cart count updates from other components
  window.addEventListener('cart-updated', (e) => {
    if (e.detail && e.detail.count !== undefined) {
      localCartCount.value = Number(e.detail.count)
    }
  })

  // Handle scroll for sticky header
  const handleScroll = () => {
    isSticky.value = window.scrollY > 20
  }

  window.addEventListener('scroll', handleScroll)

  document.addEventListener('click', (e) => {
    if (showUserMenu.value && !e.target.closest('.user-dropdown')) {
      showUserMenu.value = false
    }
    if (showMobileUserMenu.value && !e.target.closest('.mobile-user-dropdown')) {
      showMobileUserMenu.value = false
    }
    if (showCategoriesMenu.value && !e.target.closest('.categories-menu-wrapper')) {
      showCategoriesMenu.value = false
    }
  })
})
</script>

<style scoped>
.app-header,
.header-spacing-top,
.header-spacing-bottom,
.header-main {
  background-color: #f5f5f5 !important;
  background: #f5f5f5 !important;
  border: none !important;
  border-bottom: none !important;
  box-shadow: none !important;
}

.header-main {
  border-bottom: none !important;
}

.app-header.is-sticky .header-main {
  background: transparent !important;
}
</style>
